import { createPortal } from 'react-dom';
import { Button, Space, Table, Typography } from '@arco-design/web-react';
import { BsTrash3, BsGripVertical } from 'react-icons/bs';
import React, { useCallback, useMemo, useState } from 'react';
import BayToast from '@bay-ui/BayToast';
import { Tooltip } from '@bay-email/editor';
import { MultipleListingsDrawer } from '../MultipleListings/MultipleListingsDrawer';
import { t } from '@bay-email/core';
import { getListingImageUrl } from '../../../../utils/getListingImageUrl';
import {
	DndContext,
	closestCenter,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
	DragEndEvent,
} from '@dnd-kit/core';
import {
	arrayMove,
	SortableContext,
	sortableKeyboardCoordinates,
	verticalListSortingStrategy,
	useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Context for passing drag props to DragHandle
const DragContext = React.createContext<{
	attributes?: any;
	listeners?: any;
}>({});

interface ListingItem {
	id: string;
	title?: string;
	address?: string;
	city?: string;
	state?: string;
	zip?: string;
	list_price?: number;
	beds?: number;
	baths?: number;
	sq_foot?: number;
	image_data?: string[];
}

interface ListingPickerProps {
	listings: ListingItem[];
	selectedListings: ListingItem[];
	total: number;
	size: number;
	loading: boolean;
	onSelect: (selectedListings: ListingItem[]) => void;
	onPaginationChange: (params: {
		page: number;
		size: number;
		keyword?: string;
		filters?: {
			startdate?: string;
			enddate?: string;
			propertytype?: string[];
			pricemin?: string;
			pricemax?: string;
			status?: string[];
		};
		sortBy?: string;
		orderDirection?: 'ASC' | 'DESC';
	}) => void;
	disabled?: boolean;
	onOpen?: () => void;
	setActiveView?: (activeView: {
		id: 'mylistings' | 'companylistings';
		page: number;
		size: number;
	}) => void;
	openSignal?: number;
}

const DragHandle = () => {
	const { attributes, listeners } = React.useContext(DragContext);

	return (
		<BsGripVertical
			{...attributes}
			{...listeners}
			style={{
				cursor: 'move',
				color: 'rgb(92 95 98)',
				fontSize: 16,
			}}
		/>
	);
};

// Table with drag/drop functionality using @dnd-kit
const TableWrapper = (props: any) => {
	return <tbody {...props} />;
};

const SortableRow = ({
	item,
	index,
	children,
}: {
	item: ListingItem;
	index: number;
	children: React.ReactNode;
}) => {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
		useSortable({ id: item.id });

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.5 : 1,
	};

	// Store drag props in context so DragHandle can access them
	return (
		<DragContext.Provider value={{ attributes, listeners }}>
			<tr
				ref={setNodeRef}
				style={style}
				{...attributes}
				// Remove listeners from row - only apply to drag handle
			>
				{children}
			</tr>
		</DragContext.Provider>
	);
};

export function ListingPicker(props: ListingPickerProps) {
	const selectedListings = useMemo(
		() => props.selectedListings || [],
		[props.selectedListings],
	);
	// Toast lives here, not in the drawer: the drawer trigger moves in the tree
	// once listings are picked, remounting the drawer and dropping its state.
	const [toast, setToast] = useState<boolean>(false);

	const onDeleteItem = useCallback(
		(id: string) => {
			const filtered = selectedListings.filter(item => item.id !== id);
			props.onSelect(filtered);
		},
		[props.onSelect, selectedListings],
	);

	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		}),
	);

	const handleDragEnd = useCallback(
		(event: DragEndEvent) => {
			const { active, over } = event;

			if (active.id !== over?.id) {
				const oldIndex = selectedListings.findIndex(item => item.id === active.id);
				const newIndex = selectedListings.findIndex(item => item.id === over?.id);

				const reorderedListings = arrayMove(selectedListings, oldIndex, newIndex);
				props.onSelect(reorderedListings);
			}
		},
		[selectedListings, props.onSelect],
	);

	const columns = [
		{
			title: t('Content'),
			className: 'px-0',
			render: (_: any, record: ListingItem) => {
				const imageUrl = getListingImageUrl(record.image_data);

				// Format address
				const streetAddress = [record.address].filter(Boolean).join(', ');
				const displayText = streetAddress || record.title || `Listing ${record.id}`;

				// Format price
				const price =
					typeof record.list_price === 'number'
						? `$${record.list_price.toLocaleString()}`
						: record.list_price || '$0';

				return (
					<Space>
						<div
							style={{
								width: 40,
								height: 40,
								backgroundImage: `url(${imageUrl})`,
								backgroundSize: '100%',
								backgroundRepeat: 'no-repeat',
								backgroundPosition: 'center',
								border: '1px solid #e8e8e8',
							}}
						/>
						<div>
							<Tooltip content={displayText} placement="top" >
								<Typography.Text
									ellipsis
									className='m-0 pe-auto'
									style={{ width: 200, display: 'block' }}
									// Arco auto-sets a native `title` on truncated text unless told
									// otherwise, which pops its own browser tooltip alongside ours.
									// Empty string wins the merge inside Typography's Base component
									// (rest props are applied after its internal title), suppressing it.
									title=""
								>
									{displayText}
								</Typography.Text>
							</Tooltip>
							<Typography.Text
								type='secondary'
								style={{ fontSize: '12px' }}
							>
								{price}
							</Typography.Text>
						</div>
					</Space>
				);
			},
		},
		{
			title: t('Action'),
			className: 'p-0',
			render: (_: any, record: ListingItem) => {
				return (
					<div
						style={{
							display: 'flex',
							flexWrap: 'nowrap',
							justifyContent: 'flex-end',
							alignItems: 'center',
						}}
					>
						<Button
							className='bg-white'
							onClick={() => onDeleteItem(record.id)}
							icon={<BsTrash3 size={16} />}
						/>
						<DragHandle />
					</div>
				);
			},
		},
	];

	// Table with drag/drop functionality using @dnd-kit
	const components = {
		body: {
			tbody: TableWrapper,
			row: ({ record, index, ...props }: any) => (
				<SortableRow
					item={record}
					index={index}
				>
					<td {...props} />
				</SortableRow>
			),
		},
	};

	const handleConfirm = useCallback(
		(selectedIds: string[]) => {
			// Merge: keep existing selected listings that are still selected, add new ones from current page
			const existingIds = selectedListings.map(l => l.id);
			const stillSelected = selectedListings.filter(l => selectedIds.includes(l.id));
			const newSelections = props.listings.filter(
				l => selectedIds.includes(l.id) && !existingIds.includes(l.id),
			);
			props.onSelect([...stillSelected, ...newSelections]);
			setToast(true);
		},
		[props, selectedListings],
	);

	const listingDrawerTrigger = (
		<MultipleListingsDrawer
			listings={props.listings || []}
			selectedListingIds={selectedListings.map(listing => listing.id)}
			total={props.total || 0}
			size={props.size || 10}
			loading={props.loading || false}
			onPaginationChange={props.onPaginationChange}
			onConfirm={handleConfirm}
			onOpen={props.onOpen}
			disabled={props.disabled}
			t={(key: string) => t(key)}
			setActiveView={props.setActiveView}
			openSignal={props.openSignal}
		>
			<Tooltip
				content={
					props.disabled
						? t(
								"Users choosing this template will be able to select multiple listings to auto-fill this block's listing dynamic fields. Each block can feature different listings.",
							)
						: ''
				}
				disabled={!props.disabled}
			>	
				<span>
					<Button
						className={
							selectedListings.length > 0
								? 'rounded-1 w-100 border-0 p-0'
								: 'rounded-1 w-100'
						}
						type={selectedListings.length > 0 ? 'outline' : 'primary'}
						disabled={props.disabled}
					>
						{selectedListings.length > 0 ? t('Edit Listings') : t('Select Listings')}
					</Button>
				</span>
			</Tooltip>
		</MultipleListingsDrawer>
	);

	return (
		<>
			<div className='px-3 pt-4'>
				{selectedListings.length > 0 && !props.disabled ? (
					<div>
						<div className='d-flex justify-content-between align-items-center mb-2 gap-3'>
							<Typography.Text>
								{t('Selected Listings')}
							</Typography.Text>
							{listingDrawerTrigger}
						</div>
						<DndContext
							sensors={sensors}
							collisionDetection={closestCenter}
							onDragEnd={handleDragEnd}
						>
							<SortableContext
								items={selectedListings.map(item => item.id)}
								strategy={verticalListSortingStrategy}
							>
								<Table
									loading={props.loading}
									rowKey='id'
									showHeader={false}
									border={false}
									className='arco-drag-table-container-listings'
									components={components}
									columns={columns}
									data={selectedListings}
									pagination={false}
								/>
							</SortableContext>
						</DndContext>
					</div>
				) : !props.disabled && selectedListings.length === 0 ? (
					<>
						<div className='p-3 bg-yellow-100 border-yellow-200 lh-sm rounded-1 mb-3'>
							{t('This block contains dynamic listing fields. Select listings to automatically pull in the correct property data.')}
						</div>
					</>
				) : null}
				{selectedListings.length === 0 && listingDrawerTrigger}
			</div>

			{createPortal(
				<BayToast
					show={toast}
					onClose={() => setToast(false)}
					text={t('Listing information added successfully!')}
					variant='success'
				/>,
				document.body,
			)}
		</>
	);
}
